<template>
	<nuxt-link v-bind="linkAttributes">
		<slot />
	</nuxt-link>
</template>

<script setup lang="ts">
/*
 * Replacement for SB's built-in rich text link renderer. Differences to SB defaults:
 * 1. This is using NuxtLink for nicer SPA linking
 * 2. SB links always have `rel="noopener noreferrer nofollow"` - we don't want those
 * 3. SB links use the cached `href` (aka cached_url) for story links (see `getLinkHref` below).
 * 4. Attributes SB links don't recognise are JSON-serialized onto the anchor, so a resolved `story` object would end up as a `story="{...}"` attribute in the markup.
 */
import type {SbVueRichTextProps} from "@storyblok/vue";
import formatSbUrl from "@pkg/shared/utils/format-sb-url";

// eslint-disable-next-line vue/prop-name-casing -- Lint issues come from the library
const properties = defineProps<SbVueRichTextProps["link"]>();

const linkAttributes = computed(() => {
	const attributes = properties.attrs;
	const target = getStringAttribute(attributes, "target");
	const custom =
		typeof attributes?.["custom"] === "object" && attributes["custom"] !== null
			? (attributes["custom"] as Record<string, unknown>)
			: {};

	return {
		...custom,
		to: getLinkHref(attributes),
		target,
		rel: target === "_blank" ? "noopener" : undefined,
		title: getStringAttribute(attributes, "title"),
	};
});

function getLinkHref(attrs?: Record<string, unknown>) {
	type LinkStory = {url: string};

	const linkData = {
		// This is the cached link value that almost never gets updated after being initially set
		cached_url: getStringAttribute(attrs, "href") ?? "",
		// Whether the link is an external link, story, email address etc
		linktype: getStringAttribute(attrs, "linktype") ?? "url",
		// This is set on external URLs but not on story links
		url: getStringAttribute(attrs, "url") ?? "",
		anchor: getStringAttribute(attrs, "anchor"),
		story: attrs?.["story"] as LinkStory | undefined,
	};

	return formatSbUrl(linkData);
}

function getStringAttribute(
	attrs: Record<string, unknown> | undefined,
	key: string,
) {
	const value = attrs?.[key];

	return typeof value === "string" && value.length > 0 ? value : undefined;
}
</script>
